import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { WindowRef } from '@boels/helpers';
import { GlobalMessageType } from '@boels/models';
import { CultureGuard, GlobalMessageFacade } from '@boels/shared-state';
import { Observable } from 'rxjs';

const productIndicators: string[] = ['p', 'c', 'category', 'product', 'search'];

const productMatcher = (url: UrlSegment[]) => {
  return url
    .map((item: UrlSegment) => item.path)
    .filter((path: string) => productIndicators.indexOf(path) !== -1)
    .map((value: string) => {
      return value;
    }).length
    ? { consumed: [] }
    : null;
};

const contentMatcher = (url: UrlSegment[]) => {
  return url
    .map((url: UrlSegment) => url.path)
    .filter((path: string) => productIndicators.indexOf(path) !== -1)?.length
    ? null
    : { consumed: [] };
};

const BoelsAuthGuard: CanActivateFn = (
  nextRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
): Observable<boolean | UrlTree> | boolean => {
  const windowRef = inject(WindowRef);
  const isBrowser = windowRef.isBrowser();
  const fragment = nextRoute.fragment;

  if (isBrowser && fragment) {
    const msalGuard = inject(MsalGuard);
    const includesCode = msalGuard.includesCode(fragment);

    return includesCode
      ? msalGuard.canActivateChild(nextRoute, currentState)
      : true;
  }

  return true;
};

const BoelsPdpRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> | boolean => {
  const queryParams = route.queryParams;
  if (queryParams && queryParams['status'] === 'pdp_redirect') {
    const messageFacade = inject(GlobalMessageFacade);
    messageFacade.add(
      { key: 'message.productNotFound' },
      GlobalMessageType.MSG_TYPE_ERROR,
      true,
    );

    const router = inject(Router);
    const urlTree = router.createUrlTree([state.url.split('?')[0]], {
      queryParams: {},
      queryParamsHandling: 'merge',
      preserveFragment: true,
    });

    router.navigateByUrl(urlTree, { replaceUrl: true });
  }
  return true;
};

export const proxyRoutes: Route[] = [
  {
    path: '',
    children: [
      {
        path: 'countryselection',
        loadComponent: () =>
          import('@boels/components').then(
            (m) => m.LanguageSelectorPageComponent,
          ),
        canActivate: [BoelsAuthGuard],
        runGuardsAndResolvers: 'always',
      },
      {
        matcher: contentMatcher,
        loadChildren: () => import('./home.routes').then((m) => m.homeRoutes),
        canActivate: [BoelsAuthGuard, CultureGuard],
        runGuardsAndResolvers: 'always',
      },
      {
        matcher: productMatcher,
        loadChildren: () =>
          import('./product.routes').then((m) => m.productRoutes),
        canActivate: [BoelsAuthGuard, CultureGuard, BoelsPdpRedirectGuard],
        runGuardsAndResolvers: 'always',
      },
    ],
  },
];
